import { useState } from "react";
import axios from "axios";
import { Search, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

function WidgetEmbed() {
  const [registration, setRegistration] = useState("");
  const [loading, setLoading] = useState(false);
  const [vehicleData, setVehicleData] = useState(null);
  const [error, setError] = useState(null);

  // Get theme from URL params
  const urlParams = new URLSearchParams(window.location.search);
  const theme = urlParams.get('theme') || 'light';
  const isDark = theme === 'dark';

  const handleSearch = async () => {
    if (!registration || registration.length < 2) {
      setError("Please enter a registration");
      return;
    }

    setLoading(true);
    setError(null);
    setVehicleData(null);

    try {
      const response = await axios.post(
        `${API}/vehicle/lookup`,
        { registration: registration.toUpperCase().replace(/\s/g, '') },
        { withCredentials: true }
      );
      setVehicleData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to fetch vehicle data");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className={`min-h-screen p-4 ${isDark ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`}>
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mb-4">
          <h2 className="text-lg font-bold mb-1">UK Vehicle Tax Checker</h2>
          <p className="text-xs text-gray-500">Check any UK vehicle</p>
        </div>

        {/* Input */}
        <div className="mb-3">
          <div className="uk-plate-container">
            <div className="uk-plate uk-plate-compact">
              <input
                type="text"
                value={registration}
                onChange={(e) => setRegistration(e.target.value.toUpperCase())}
                onKeyPress={handleKeyPress}
                placeholder="ABC123"
                maxLength="8"
                className="uk-plate-input uk-plate-input-compact"
              />
            </div>
          </div>
        </div>

        <Button
          onClick={handleSearch}
          disabled={loading}
          className={`w-full ${isDark ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-900 hover:bg-gray-800'}`}
          size="sm"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Checking...
            </>
          ) : (
            <>
              <Search className="mr-2 h-4 w-4" />
              Check Vehicle
            </>
          )}
        </Button>

        {/* Error */}
        {error && (
          <div className="mt-3 p-2 bg-red-100 border border-red-300 rounded text-red-700 text-xs text-center">
            {error}
          </div>
        )}

        {/* Results */}
        {vehicleData && (
          <div className="mt-4 space-y-3">
            {/* Make/Model */}
            <div className={`p-3 rounded border ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
              <div className="text-xs text-gray-500 mb-1">Vehicle</div>
              <div className="font-semibold text-sm">
                {vehicleData.year_of_manufacture} {vehicleData.make} {vehicleData.model || ''}
              </div>
            </div>

            {/* Tax Status */}
            <div className={`p-3 rounded border ${
              vehicleData.tax_status === 'Taxed' 
                ? 'border-green-300 bg-green-50' 
                : vehicleData.tax_status === 'SORN'
                ? 'border-red-300 bg-red-50'
                : 'border-red-300 bg-red-50'
            }`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold">Tax Status</span>
                <span className={`text-sm font-bold ${
                  vehicleData.tax_status === 'Taxed' ? 'text-green-700' : 'text-red-700'
                }`}>
                  {vehicleData.tax_status}
                </span>
              </div>
            </div>

            {/* MOT Status */}
            <div className={`p-3 rounded border ${
              vehicleData.mot_status === 'Valid' 
                ? 'border-green-300 bg-green-50' 
                : 'border-red-300 bg-red-50'
            }`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold">MOT Status</span>
                <span className={`text-sm font-bold ${
                  vehicleData.mot_status === 'Valid' ? 'text-green-700' : 'text-red-700'
                }`}>
                  {vehicleData.mot_status}
                </span>
              </div>
            </div>

            {/* Footer */}
            <div className="text-center pt-2">
              <a 
                href={`${window.location.origin}?reg=${vehicleData.registration}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:underline"
              >
                View full details →
              </a>
            </div>
          </div>
        )}

        {/* Branding */}
        <div className="mt-4 text-center">
          <a 
            href={window.location.origin}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            Powered by Is It Taxed
          </a>
        </div>
      </div>
    </div>
  );
}

export default WidgetEmbed;
